import { useEffect, useState, useRef, useCallback } from 'react'
import {
  Row,
  Col,
  Tree,
  Button,
  Table,
  Input,
  Select,
  TreeSelect,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Empty,
  Typography,
  Checkbox,
  Popconfirm,
  Dropdown,
} from 'antd'
import type { MenuProps } from 'antd'
import {
  PlusOutlined,
  FileTextOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  ImportOutlined,
} from '@ant-design/icons'
import type { TreeDataNode } from 'antd'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'
import * as testCaseApi from '@/api/testCases'
import * as aiApi from '@/api/aiTestCases'
import type { TestCase, TestCaseModule } from '@/types'
import type { AITestTask, AITestCaseItem } from '@/api/aiTestCases'
import { useProjectStore } from '@/stores/project'
import { useThemeStore } from '@/stores/theme'

const { Title } = Typography

/** 优先级颜色 */
const priorityColor: Record<string, string> = {
  P0: 'red',
  P1: 'orange',
  P2: 'blue',
  P3: 'default',
}

/** 状态颜色 */
const statusColor: Record<string, string> = {
  draft: 'default',
  reviewing: 'processing',
  ready: 'success',
  deprecated: 'error',
}

const statusText: Record<string, string> = {
  draft: '草稿',
  reviewing: '评审中',
  ready: '就绪',
  deprecated: '已废弃',
}

export default function TestCases() {
  const { isDark } = useThemeStore()
  const [modules, setModules] = useState<TestCaseModule[]>([])
  const [cases, setCases] = useState<TestCase[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedModule, setSelectedModule] = useState<number | null>(null)
  const [keyword, setKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [priority, setPriority] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCase, setEditingCase] = useState<TestCase | null>(null)
  const [form] = Form.useForm()

  // AI 用例导入
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [aiTasks, setAiTasks] = useState<AITestTask[]>([])
  const [aiTasksLoading, setAiTasksLoading] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [aiCases, setAiCases] = useState<AITestCaseItem[]>([])
  const [aiCasesLoading, setAiCasesLoading] = useState(false)
  const [selectedAiIds, setSelectedAiIds] = useState<number[]>([])
  const [importing, setImporting] = useState(false)

  // 模块操作
  const [moduleModalOpen, setModuleModalOpen] = useState(false)
  const [editingModule, setEditingModule] = useState<TestCaseModule | null>(null)
  const [moduleForm] = Form.useForm()
  const [contextMenuModule, setContextMenuModule] = useState<TestCaseModule | null>(null)
  const treeContainerRef = useRef<HTMLDivElement>(null)

  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  /** 加载模块树 */
  const loadModules = useCallback(async () => {
    try {
      const data = await testCaseApi.getModuleTree(projectId)
      setModules(data)
    } catch (e) { console.error('[TestCases] 操作失败:', e) }
  }, [projectId])

  /** 加载用例列表 */
  const loadCases = async () => {
    setLoading(true)
    try {
      const res = await testCaseApi.listCases({
        project_id: projectId,
        module_id: selectedModule || undefined,
        page,
        page_size: pageSize,
        keyword: searchKeyword || undefined,
        priority: priority || undefined,
        status: statusFilter || undefined,
      })
      setCases(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadModules()
  }, [loadModules])

  useEffect(() => {
    loadCases()
  }, [selectedModule, page, pageSize, searchKeyword, priority, statusFilter, projectId])

  const handleSearch = () => {
    setSearchKeyword(keyword.trim())
    setPage(1)
  }

  /** 模块树数据转换（Tree 展示用） */
  const treeData: TreeDataNode[] = (function convert(list: TestCaseModule[]): TreeDataNode[] {
    return list.map((m) => ({
      key: m.id,
      title: (
        <Space>
          <FolderOutlined style={{ color: 'var(--au-primary)' }} />
          <span>{m.name}</span>
        </Space>
      ),
      children: m.children ? convert(m.children) : undefined,
    }))
  })(modules)

  /** 模块树数据（TreeSelect 下拉用，纯文本 title） */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const moduleSelectTreeData = (function convert(list: TestCaseModule[]): any[] {
    return list.map((m) => ({
      value: m.id,
      title: m.name,
      children: m.children ? convert(m.children) : undefined,
    }))
  })(modules)

  /** 表格列定义 */
  const columns = [
    {
      title: <div style={{ textAlign: 'center' }}>用例编号</div>,
      dataIndex: 'case_no',
      width: 80,
      render: (v: string) => (
        <span style={{ fontFamily: 'monospace', color: 'var(--au-slate-muted)', fontSize: 14 }}>{v}</span>
      ),
    },
    { title: <div style={{ textAlign: 'center' }}>标题</div>, dataIndex: 'title', width: 166, ellipsis: true, render: (v: string) => <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v}</span> },
    {
      title: <div style={{ textAlign: 'center' }}>优先级</div>,
      dataIndex: 'priority',
      width: 60,
      render: (v: string) => <Tag color={priorityColor[v]}>{v}</Tag>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>状态</div>,
      dataIndex: 'status',
      width: 65,
      render: (v: string) => <Tag color={statusColor[v]}>{statusText[v] || v}</Tag>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>前置条件</div>,
      dataIndex: 'precondition',
      width: 180,
      ellipsis: true,
      render: (v: string) => {
        if (!v) return <span style={{ color: 'var(--au-slate-muted)' }}>-</span>
        const lines = v.split('\n').filter((l) => l.trim()).map((l) => `• ${l.trim()}`)
        return (
          <span style={{
            color: isDark ? '#A5B4FC' : '#4f46e5',
            background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
            padding: '4px 6px',
            borderRadius: 4,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            textIndent: 0,
            display: 'inline-block',
          }}>
            {lines.join('\n')}
          </span>
        )
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>测试步骤</div>,
      dataIndex: 'steps',
      width: 200,
      ellipsis: true,
      render: (v: string) => {
        let text = '-'
        try {
          const steps = JSON.parse(v)
          if (Array.isArray(steps) && steps.length > 0) {
            text = steps.map((s: { step: number; action: string }) => `${s.step}. ${s.action}`).join('\n')
          }
        } catch (e) { console.error('[TestCases] 操作失败:', e) }
        return (
          <span style={{
            color: isDark ? '#FCD34D' : '#b45309',
            background: isDark ? 'rgba(251,191,36,0.15)' : '#fffbeb',
            padding: '4px 6px',
            borderRadius: 4,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            textIndent: 0,
            display: 'inline-block',
          }}>
            {text}
          </span>
        )
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>预期结果</div>,
      dataIndex: 'expected_result',
      width: 180,
      ellipsis: true,
      render: (v: string) => {
        if (!v) return <span style={{ color: 'var(--au-slate-muted)' }}>-</span>
        const lines = v.split('\n').filter((l) => l.trim()).map((l) => `• ${l.trim()}`)
        return (
          <span style={{
            color: isDark ? '#6EE7B7' : '#166534',
            background: isDark ? 'rgba(16,185,129,0.15)' : '#f0fdf4',
            padding: '4px 6px',
            borderRadius: 4,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            textIndent: 0,
            display: 'inline-block',
          }}>
            {lines.join('\n')}
          </span>
        )
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>版本</div>,
      dataIndex: 'version',
      width: 64,
      render: (v: number) => `v${v}`,
    },
    {
      title: <div style={{ textAlign: 'center' }}>操作</div>,
      width: 80,
      render: (_: unknown, record: TestCase) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingCase(record)
              form.setFieldsValue(record)
              setModalOpen(true)
            }}
            style={{ padding: 0, height: 22 }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此用例？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger style={{ padding: 0, height: 22 }}>
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ]

  /** 提交表单 */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (editingCase) {
        await testCaseApi.updateCase(editingCase.id, values, projectId)
        message.success('更新成功')
      } else {
        await testCaseApi.createCase({
          ...values,
          project_id: projectId,
          creator_id: 1,
        })
        message.success('创建成功')
      }
      setModalOpen(false)
      form.resetFields()
      setEditingCase(null)
      loadCases()
    } catch (e) { console.error('[TestCases] 操作失败:', e) }
  }

  /** 删除用例 */
  const handleDelete = async (id: number) => {
    try {
      await testCaseApi.deleteCase(id, projectId)
      message.success('删除成功')
      loadCases()
    } catch (e) { console.error('[TestCases] 操作失败:', e) }
  }

  /** 打开新增模块弹窗 */
  const openCreateModuleModal = (parentId: number | null = null) => {
    setEditingModule(null)
    moduleForm.resetFields()
    moduleForm.setFieldValue('parent_id', parentId)
    setModuleModalOpen(true)
  }

  /** 打开编辑模块弹窗 */
  const openEditModuleModal = (mod: TestCaseModule) => {
    setEditingModule(mod)
    moduleForm.setFieldsValue({ name: mod.name })
    setModuleModalOpen(true)
  }

  /** 提交模块表单 */
  const handleModuleSubmit = async () => {
    try {
      const values = await moduleForm.validateFields()
      if (editingModule) {
        await testCaseApi.updateModule(editingModule.id, { name: values.name })
        message.success('模块名称更新成功')
      } else {
        await testCaseApi.createModule({
          project_id: projectId,
          parent_id: values.parent_id || null,
          name: values.name,
          sort: 0,
        })
        message.success('模块创建成功')
      }
      setModuleModalOpen(false)
      moduleForm.resetFields()
      setEditingModule(null)
      loadModules()
    } catch (e) { console.error('[TestCases] 操作失败:', e) }
  }

  /** 删除模块 */
  const handleDeleteModule = async (mod: TestCaseModule) => {
    try {
      await testCaseApi.deleteModule(mod.id, projectId)
      message.success('模块已删除')
      if (selectedModule === mod.id) {
        setSelectedModule(null)
      }
      loadModules()
    } catch (e) { console.error('[TestCases] 操作失败:', e) }
  }

  /** 右键菜单项 */
  const buildContextMenuItems = (mod: TestCaseModule): MenuProps['items'] => {
    return [
      {
        key: 'edit',
        label: '修改名称',
        onClick: () => openEditModuleModal(mod),
      },
      {
        key: 'delete',
        label: '删除',
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: '确认删除',
            content: `确定要删除模块「${mod.name}」吗？其下所有子模块将被一起删除。`,
            okText: '确定删除',
            okType: 'danger',
            cancelText: '取消',
            wrapClassName: 'aurora-modal-wrap',
            className: 'aurora-modal',
            onOk: () => handleDeleteModule(mod),
          })
        },
      },
    ]
  }

  /** 树节点右键处理 */
  const handleTreeRightClick = (info: { event: React.MouseEvent; node: { key: React.Key } }) => {
    const { node } = info
    const key = Number(node.key)
    // 从 modules 中查找对应模块
    const findModule = (list: TestCaseModule[]): TestCaseModule | null => {
      for (const m of list) {
        if (m.id === key) return m
        if (m.children) {
          const found = findModule(m.children)
          if (found) return found
        }
      }
      return null
    }
    const mod = findModule(modules)
    if (mod) {
      setContextMenuModule(mod)
    }
  }

  /** 打开导入弹窗，加载 AI 任务列表 */
  const openImportModal = async () => {
    setImportModalOpen(true)
    setSelectedTaskId(null)
    setAiCases([])
    setSelectedAiIds([])
    setAiTasksLoading(true)
    try {
      const res = await aiApi.listTasks({ page: 1, page_size: 100, status: 'completed' })
      setAiTasks((res.data || []).filter((t: AITestTask) => t.total_cases > 0))
    } finally {
      setAiTasksLoading(false)
    }
  }

  /** 选择 AI 任务后加载该任务下的用例 */
  const handleSelectTask = async (taskId: number) => {
    setSelectedTaskId(taskId)
    setSelectedAiIds([])
    setAiCasesLoading(true)
    try {
      const res = await aiApi.listCases(taskId)
      setAiCases(res || [])
    } finally {
      setAiCasesLoading(false)
    }
  }

  /** 执行导入 */
  const handleImport = async () => {
    if (selectedAiIds.length === 0) {
      message.warning('请选择要导入的用例')
      return
    }
    setImporting(true)
    try {
      const res = await testCaseApi.importFromAI({ project_id: projectId, ai_case_ids: selectedAiIds })
      const msg = `成功导入 ${res.imported} 条用例`
      message.success(res.skipped > 0 ? `${msg}，跳过 ${res.skipped} 条重复` : msg)
      setImportModalOpen(false)
      loadModules()
      loadCases()
    } catch {
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="aurora-page aurora-page--fit">
      {/* 页面标题 */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
            测试用例
          </Title>
          <Space>
            <Button onClick={openImportModal}>
              导入AI用例
            </Button>
            <Button type="primary" onClick={() => {
              setEditingCase(null)
              form.resetFields()
              setModalOpen(true)
            }}>
              新增用例
            </Button>
          </Space>
      </div>

      <Row gutter={16} style={{ flex: 1, overflow: 'hidden' }}>
        {/* 左侧模块树 */}
        <Col xs={24} md={5} lg={4} style={{ height: '100%' }}>
          <div className="content-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="content-card-header" style={{ flexShrink: 0 }}>
              <div className="content-card-title">
                <FolderOutlined style={{ marginRight: 8, color: 'var(--au-primary)' }} />
                用例模块
              </div>
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openCreateModuleModal(null)}
              />
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px' }}>
              {treeData.length > 0 ? (
                <Dropdown
                  menu={{ items: contextMenuModule ? buildContextMenuItems(contextMenuModule) : [] }}
                  trigger={['contextMenu']}
                  open={contextMenuModule !== null}
                  onOpenChange={(open) => { if (!open) setContextMenuModule(null) }}
                >
                  <div ref={treeContainerRef}>
                    <Tree
                      treeData={treeData}
                      defaultExpandAll
                  onSelect={(keys) => {
                    setSelectedModule(keys[0] ? Number(keys[0]) : null)
                    setPage(1)
                  }}
                  onRightClick={handleTreeRightClick}
                  showLine={{ showLeafIcon: false }}
                />
                  </div>
                </Dropdown>
              ) : (
                <Empty description="暂无模块" />
              )}
            </div>
          </div>
        </Col>

        {/* 右侧用例列表 */}
        <Col xs={24} md={19} lg={20} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* 搜索栏 */}
          <div className="search-bar" style={{ flexShrink: 0 }}>
            <Row gutter={[12, 0]} align="middle">
              <Col>
                <Select
                  placeholder="优先级"
                  allowClear
                  style={{ width: 120 }}
                  value={priority || undefined}
                  onChange={(v) => setPriority(v || '')}
                  options={[
                    { value: 'P0', label: 'P0' },
                    { value: 'P1', label: 'P1' },
                    { value: 'P2', label: 'P2' },
                    { value: 'P3', label: 'P3' },
                  ]}
                />
              </Col>
              <Col>
                <Select
                  placeholder="状态"
                  allowClear
                  style={{ width: 120 }}
                  value={statusFilter || undefined}
                  onChange={(v) => setStatusFilter(v || '')}
                  options={[
                    { value: 'draft', label: '草稿' },
                    { value: 'reviewing', label: '评审中' },
                    { value: 'ready', label: '就绪' },
                    { value: 'deprecated', label: '已废弃' },
                  ]}
                />
              </Col>
              <Col flex="auto">
                <Input
                  placeholder="搜索用例标题"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onPressEnter={handleSearch}
                  allowClear
                  style={{ minWidth: 100 }}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={handleSearch}
                  style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
                >
                  查询
                </Button>
              </Col>
            </Row>
          </div>

          {/* 表格 */}
          <div className="content-card" style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Table
                columns={columns}
                dataSource={cases}
                rowKey="id"
                loading={loading}
                size="middle"
                scroll={{ y: '100%' }}
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  showSizeChanger: true,
                  showTotal: (t) => `共 ${t} 条`,
                  onChange: (p, ps) => {
                    setPage(p)
                    setPageSize(ps)
                  },
                }}
              />
            </div>
          </div>
        </Col>

        {/* 新增/编辑用例弹窗 */}
        <Modal
          title={editingCase ? '编辑用例' : '新增用例'}
          open={modalOpen}
          onOk={handleSubmit}
          onCancel={() => {
            setModalOpen(false)
            form.resetFields()
            setEditingCase(null)
          }}
          width={640}
          okText="保存"
          cancelText="取消"
          wrapClassName="aurora-modal-wrap"
          className="aurora-modal"
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="title"
              label="用例标题"
              rules={[{ required: true, message: '请输入用例标题' }]}
            >
              <Input placeholder="请输入用例标题" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="priority" label="优先级" initialValue="P2">
                  <Select
                    options={[
                      { value: 'P0', label: 'P0 - 最高' },
                      { value: 'P1', label: 'P1 - 高' },
                      { value: 'P2', label: 'P2 - 中' },
                      { value: 'P3', label: 'P3 - 低' },
                    ]}
                    style={{ background: isDark ? 'var(--bg)' : undefined, color: isDark ? 'var(--text)' : undefined }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="status" label="状态" initialValue="draft">
                  <Select
                    options={[
                      { value: 'draft', label: '草稿' },
                      { value: 'reviewing', label: '评审中' },
                      { value: 'ready', label: '就绪' },
                      { value: 'deprecated', label: '已废弃' },
                    ]}
                    style={{ background: isDark ? 'var(--bg)' : undefined, color: isDark ? 'var(--text)' : undefined }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="precondition" label="前置条件">
              <Input.TextArea rows={2} placeholder="请输入前置条件" />
            </Form.Item>
            <Form.Item name="steps" label="测试步骤">
              <Input.TextArea rows={4} placeholder="请输入测试步骤（每行一步）" />
            </Form.Item>
            <Form.Item name="expected_result" label="预期结果">
              <Input.TextArea rows={3} placeholder="请输入预期结果" />
            </Form.Item>
          </Form>
        </Modal>

        {/* AI 用例导入弹窗 */}
        <Modal
          title="导入 AI 生成的用例"
          open={importModalOpen}
          onCancel={() => setImportModalOpen(false)}
          width={900}
          wrapClassName="aurora-modal-wrap"
          className="aurora-modal"
          footer={[
            <Button key="cancel" onClick={() => setImportModalOpen(false)}>取消</Button>,
            <Button key="import" type="primary" loading={importing}
              disabled={selectedAiIds.length === 0}
              onClick={handleImport}>
              导入选中 ({selectedAiIds.length} 条)
            </Button>,
          ]}
          destroyOnClose
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <Select
                placeholder="选择 AI 生成任务"
                style={{ flex: 1 }}
                value={selectedTaskId}
                onChange={handleSelectTask}
                loading={aiTasksLoading}
                showSearch
                optionFilterProp="label"
                options={aiTasks.map((t) => ({
                  value: t.id,
                  label: `${t.title || '未命名'} (${t.total_cases} 条用例) - ${t.create_time?.split('T')[0]}`,
                }))}
                notFoundContent={aiTasksLoading ? '加载中...' : '暂无完成的 AI 生成任务'}
              />
              <Button onClick={() => { setImportModalOpen(false); window.location.href = '/ai-test-cases' }}>
                去生成用例
              </Button>
            </div>
          </div>

          {selectedTaskId ? (
            aiCasesLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Empty description="加载用例中..." />
              </div>
            ) : aiCases.length === 0 ? (
              <Empty description="该任务暂无用例" />
            ) : (
              <>
                <div style={{ marginBottom: 8 }}>
                  <Checkbox
                    checked={selectedAiIds.length === aiCases.length}
                    indeterminate={selectedAiIds.length > 0 && selectedAiIds.length < aiCases.length}
                    onChange={(e) => {
                      setSelectedAiIds(e.target.checked ? aiCases.map((c) => c.id) : [])
                    }}
                  >
                    全选 ({aiCases.length} 条用例)
                  </Checkbox>
                </div>
                <Table
                  rowKey="id"
                  dataSource={aiCases}
                  loading={aiCasesLoading}
                  size="small"
                  pagination={false}
                  scroll={{ y: 400 }}
                  columns={[
                    {
                      title: '',
                      width: 40,
                      render: (_, record) => (
                        <Checkbox
                          checked={selectedAiIds.includes(record.id)}
                          onChange={(e) => {
                            setSelectedAiIds((prev) =>
                              e.target.checked
                                ? [...prev, record.id]
                                : prev.filter((id) => id !== record.id),
                            )
                          }}
                        />
                      ),
                    },
                    { title: '模块', dataIndex: 'module', width: 100, ellipsis: true },
                    { title: '子模块', dataIndex: 'sub_module', width: 100, ellipsis: true },
                    { title: '标题', dataIndex: 'title', ellipsis: true },
                    {
                      title: '优先级', dataIndex: 'priority', width: 70,
                      render: (v: string) => <Tag color={priorityColor[v] || 'default'}>{v}</Tag>,
                    },
                  ]}
                />
              </>
            )
          ) : (
            <Empty description="请先选择一个 AI 生成任务" style={{ padding: 40 }} />
          )}
        </Modal>

        {/* 模块新增/编辑弹窗 */}
        <Modal
          title={editingModule ? '修改模块名称' : '新增用例模块'}
          open={moduleModalOpen}
          onOk={handleModuleSubmit}
          onCancel={() => { setModuleModalOpen(false); moduleForm.resetFields(); setEditingModule(null) }}
          width={480}
          okText="保存"
          cancelText="取消"
          wrapClassName="aurora-modal-wrap"
          className="aurora-modal"
          destroyOnClose
        >
          <Form form={moduleForm} layout="vertical" style={{ marginTop: 4 }}>
            {!editingModule && (
              <Form.Item name="parent_id" label="上级模块">
                <TreeSelect
                  allowClear
                  placeholder="留空则为根模块"
                  treeDefaultExpandAll
                  treeData={moduleSelectTreeData}
                />
              </Form.Item>
            )}
            <Form.Item name="name" label="模块名称" rules={[{ required: true, message: '请输入模块名称' }]}>
              <Input placeholder="请输入模块名称" maxLength={128} />
            </Form.Item>
          </Form>
        </Modal>
      </Row>
    </div>
  )
}
